/*
  Video feature — playback for this section only.

  One video per section, so there is nothing to arbitrate: it plays while
  visible if autoplay is on, or when the visitor presses play, and pauses when
  it leaves the screen. Deliberately self-contained — sharing this logic with
  the reel carousel is what made both of them unpredictable.

  Lazy: the file has no src until it first needs to play, so it never competes
  with above-the-fold content for bandwidth.
*/

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var VISIBLE = 0.5;

  function Feature(root) {
    this.root = root;
    this.video = root.querySelector('video');
    if (!this.video) return;

    this.src = root.getAttribute('data-video-src') || '';
    this.autoplay = root.getAttribute('data-autoplay') !== 'false';
    this.playWithSound = root.getAttribute('data-muted') === 'false';

    this.attached = !this.src;
    this.visible = false;
    this.pressedPause = false;
    this.pressedPlay = false;

    this.video.playsInline = true;
    this.video.setAttribute('playsinline', '');
    if (this.autoplay) this.mute();

    var self = this;

    this.toggles = Array.prototype.slice.call(root.querySelectorAll('[data-viewport-video-toggle]'));
    this.idle = null;

    // State mirrors the element, never our intent.
    this.video.addEventListener('play', function () {
      root.setAttribute('data-playing', 'true');
      /*
        Latched, not tied to [data-playing]: the poster must stay gone once the
        footage has shown a frame. Bringing it back on pause looks exactly like
        the video resetting itself.
      */
      root.setAttribute('data-started', 'true');
      self.syncLabels();
      self.reveal(false);
    });
    this.video.addEventListener('pause', function () {
      root.setAttribute('data-playing', 'false');
      self.syncLabels();
      // Pin it on — this is the state the visitor needs a control in most.
      self.reveal(true);
    });
    this.video.addEventListener('ended', function () {
      root.setAttribute('data-playing', 'false');
      self.syncLabels();
      self.reveal(true);
    });

    this.toggles.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        self.press();
      });
    });

    /*
      Touch devices never hover, so a tap anywhere on the frame surfaces the
      control rather than leaving the visitor with nothing to aim at.
    */
    root.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse') return;
      if (event.target.closest && event.target.closest('[data-viewport-video-toggle]')) return;
      self.reveal(false);
    });

    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          self.setVisible(entry.isIntersecting && entry.intersectionRatio >= VISIBLE);
        });
      },
      { threshold: [0, VISIBLE] }
    );
    this.observer.observe(root);

    root.videoFeature = this;
  }

  /* Announce the action the press performs, not a fixed string. */
  Feature.prototype.syncLabels = function () {
    var playing = !this.video.paused;
    this.toggles.forEach(function (button) {
      var next = button.getAttribute(playing ? 'data-label-pause' : 'data-label-play');
      if (next) button.setAttribute('aria-label', next);
    });
  };

  /*
    Show the control. `pin` keeps it up indefinitely (paused state); otherwise
    it lingers and then recedes, but only while the video is still running.
  */
  Feature.prototype.reveal = function (pin) {
    this.root.setAttribute('data-controls', 'true');
    window.clearTimeout(this.idle);
    if (pin) return;
    var self = this;
    this.idle = window.setTimeout(function () {
      if (!self.video.paused) self.root.setAttribute('data-controls', 'false');
    }, 2200);
  };

  Feature.prototype.mute = function () {
    this.video.muted = true;
    this.video.setAttribute('muted', '');
  };

  /*
    Assigning src is enough to begin loading. Never call load() here — it
    resets the element and aborts a play() already in flight, so the first
    press, which is also the first load, would fail every time.
  */
  Feature.prototype.attach = function () {
    if (this.attached) return;
    this.attached = true;
    this.video.src = this.src;
  };

  Feature.prototype.play = function () {
    if (!this.video.paused) return;
    this.attach();

    var self = this;
    var promise = this.video.play();
    if (!promise || !promise.catch) return;

    promise.catch(function () {
      // Refused unmuted playback: fall back to muted rather than doing nothing.
      if (self.video.muted) return;
      self.mute();
      var retry = self.video.play();
      if (retry && retry.catch) retry.catch(function () {});
    });
  };

  Feature.prototype.pause = function () {
    if (this.video.paused) return;
    this.video.pause();
  };

  Feature.prototype.apply = function () {
    if (!this.visible || this.pressedPause) {
      this.pause();
      return;
    }
    if (this.pressedPlay || (this.autoplay && !REDUCED.matches)) this.play();
    else this.pause();
  };

  Feature.prototype.setVisible = function (visible) {
    if (this.visible === visible) return;
    this.visible = visible;
    this.root.setAttribute('data-in-view', visible ? 'true' : 'false');
    // Press state applies to the current viewing only.
    if (!visible) {
      this.pressedPause = false;
      this.pressedPlay = false;
    }
    this.apply();
  };

  Feature.prototype.press = function () {
    if (this.video.paused) {
      this.pressedPlay = true;
      this.pressedPause = false;
      // A press is a user gesture — the one moment sound is allowed.
      if (this.playWithSound) {
        this.video.muted = false;
        this.video.removeAttribute('muted');
      }
      this.play();
    } else {
      this.pressedPause = true;
      this.pressedPlay = false;
      this.pause();
    }
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-video-feature]').forEach(function (el) {
      if (el.videoFeature) return;
      new Feature(el);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init.bind(null, undefined));

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
